import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { adminApi } from '../../services/adminApi';
import { Building2, TrendingUp, Loader2, GraduationCap, Users, Truck } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, RadialBarChart, RadialBar, Legend } from 'recharts';

export default function BranchPerformance() {
  const ctx = useOutletContext();
  const [schools, setSchools] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([adminApi.getSchools(), adminApi.getReservations()])
      .then(([s, r]) => { setSchools(s); setReservations(r); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-400" /></div>;

  const branches = schools.map(s => {
    const sRes = reservations.filter(r => r.school === s.name);
    const paid = sRes.filter(r => r.deposit_paid).length;
    const revenue = paid * 30;
    const conversion = sRes.length ? Math.round((paid / sRes.length) * 100) : 0;
    return {
      ...s,
      reservations: sRes.length,
      paid_deposits: paid,
      revenue,
      conversion,
      items: sRes.reduce((a, r) => a + (r.item_count || 0), 0),
    };
  }).sort((a, b) => b.reservations - a.reservations);

  const chartData = branches.map(b => ({ name: b.short || b.name.substring(0, 10), reservations: b.reservations, revenue: b.revenue }));
  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#ef4444', '#84cc16', '#6366f1', '#f97316'];

  return (
    <div data-testid="admin-branch-performance">
      <div className="mb-6">
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Branch Performance</h1>
        <p className={`text-sm ${ctx.textSecondary} mt-1`}>{branches.length} branches · ranked by activity</p>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className={`${ctx.cardBg} rounded-xl border ${ctx.borderColor} p-4`}>
          <Building2 className="w-5 h-5 text-blue-400 mb-2" />
          <p className="text-xl font-bold">{branches.length}</p>
          <p className={`text-xs ${ctx.textSecondary}`}>Active Branches</p>
        </div>
        <div className={`${ctx.cardBg} rounded-xl border ${ctx.borderColor} p-4`}>
          <GraduationCap className="w-5 h-5 text-emerald-400 mb-2" />
          <p className="text-xl font-bold">{branches.reduce((a, b) => a + b.reservations, 0)}</p>
          <p className={`text-xs ${ctx.textSecondary}`}>Total Reservations</p>
        </div>
        <div className={`${ctx.cardBg} rounded-xl border ${ctx.borderColor} p-4`}>
          <TrendingUp className="w-5 h-5 text-amber-400 mb-2" />
          <p className="text-xl font-bold">${branches.reduce((a, b) => a + b.revenue, 0)}</p>
          <p className={`text-xs ${ctx.textSecondary}`}>Deposit Revenue</p>
        </div>
        <div className={`${ctx.cardBg} rounded-xl border ${ctx.borderColor} p-4`}>
          <Truck className="w-5 h-5 text-purple-400 mb-2" />
          <p className="text-xl font-bold">{branches.reduce((a, b) => a + b.items, 0)}</p>
          <p className={`text-xs ${ctx.textSecondary}`}>Items in Storage</p>
        </div>
      </div>

      {/* Reservations chart */}
      <div className={`${ctx.cardBg} rounded-xl border ${ctx.borderColor} p-5 mb-6`}>
        <h3 className="text-sm font-semibold mb-4">Reservations & Revenue by Branch</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
            <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ background: '#1e2030', border: 'none', borderRadius: 8, fontSize: 12 }} />
            <Bar yAxisId="left" dataKey="reservations" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            <Bar yAxisId="right" dataKey="revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Branch ranking table */}
      <div className={`${ctx.cardBg} rounded-xl border ${ctx.borderColor} overflow-x-auto`} data-testid="branch-ranking">
        <table className="w-full text-sm">
          <thead><tr className={`border-b ${ctx.borderColor}`}>
            {['Rank', 'Branch', 'Manager', 'Reservations', 'Paid Deposits', 'Items', 'Revenue', 'Conversion'].map(h => (
              <th key={h} className={`text-left px-4 py-3 text-xs font-medium ${ctx.textSecondary} uppercase tracking-wider`}>{h}</th>
            ))}
          </tr></thead>
          <tbody>{branches.map((b, i) => (
            <tr key={b._id} className={`border-b ${ctx.borderColor} ${ctx.hoverBg}`}>
              <td className="px-4 py-3">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold" style={{ background: `${colors[i % colors.length]}20`, color: colors[i % colors.length] }}>
                  {i + 1}
                </span>
              </td>
              <td className="px-4 py-3"><p className="font-medium text-sm">{b.name}</p><p className={`text-xs ${ctx.textSecondary}`}>{b.location}</p></td>
              <td className="px-4 py-3 text-xs">{b.branch_manager}</td>
              <td className="px-4 py-3 font-medium">{b.reservations}</td>
              <td className="px-4 py-3 text-emerald-400 font-medium">{b.paid_deposits}</td>
              <td className="px-4 py-3">{b.items}</td>
              <td className="px-4 py-3 font-medium">${b.revenue}</td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full bg-gray-500/20 overflow-hidden">
                    <div className="h-full bg-blue-500" style={{ width: `${b.conversion}%` }} />
                  </div>
                  <span className="text-xs font-medium w-10 text-right">{b.conversion}%</span>
                </div>
              </td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}
